import apiClient from './apiClient'
import type { HolidayRequest } from '@/types'

/**
 * Holiday Service - manages holiday requests via API
 */
class HolidayService {
  /**
   * Get all holidays for the current user (paginated)
   */
  async getHolidays(perPage: number = 10): Promise<{ data: HolidayRequest[] }> {
    const response = await apiClient.get(`/api/auth/holidays?per_page=${perPage}`)
    return response as { data: HolidayRequest[] }
  }

  /**
   * Get holidays for a specific customer and refresh from CRM
   */
  async getCustomerHolidays(customerChargebeeId: string): Promise<{ data: HolidayRequest[] }> {
    const response = await apiClient.get(`/api/auth/holidays/customer/${customerChargebeeId}`)
    return response as { data: HolidayRequest[] }
  }

  /**
   * Get pending holidays for a specific subscription
   */
  async getPendingHolidays(subscriptionChargebeeId: string): Promise<{ data: HolidayRequest[] }> {
    const response = await apiClient.get(`/api/auth/holidays/pending/${subscriptionChargebeeId}`)
    return response as { data: HolidayRequest[] }
  }

  /**
   * Create a new holiday request
   */
  async createHoliday(
    subscriptionChargebeeId: string,
    startDate: string,
    endDate: string
  ): Promise<{ data: HolidayRequest }> {
    const response = await apiClient.post('/api/auth/holidays', {
      subscription_chargebee_id: subscriptionChargebeeId,
      start_date: startDate,
      end_date: endDate,
    })
    return response as { data: HolidayRequest }
  }

  /**
   * Update an existing holiday request
   */
  async updateHoliday(
    crmId: string,
    updates: {
      start_date?: string
      end_date?: string
      statecode?: number
      statuscode?: number
    }
  ): Promise<{ data: HolidayRequest }> {
    const response = await apiClient.patch(`/api/auth/holidays/${crmId}`, updates)
    return response as { data: HolidayRequest }
  }

  /**
   * Delete a holiday request (removes from both CRM and database)
   */
  async cancelHoliday(crmId: string): Promise<void> {
    await apiClient.delete(`/api/auth/holidays/${crmId}`)
  }
}

export default new HolidayService()
